import StatCard from '@/components/stats/StatCard';
import StatCardSkeleton from '@/components/stats/StatCardSkeleton';
import api from '@/libs/axiosInstance';
import type { AffiliateProgramStats as AffiliateProgramStatsType } from '@/types/affiliate-programs';
import { Center, Flex, Text } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';

interface AffiliateProgramStatsProps {
  programId: string;
}

const fetchAffiliateProgramStats = async (
  programId: string
): Promise<AffiliateProgramStatsType> => {
  const { data } = await api.get<AffiliateProgramStatsType>(
    `/dashboard/programs/${programId}/stats`
  );
  return data;
};

const AffiliateProgramStats: React.FC<AffiliateProgramStatsProps> = ({ programId }) => {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['affiliateProgramStats', programId],
    queryFn: () => fetchAffiliateProgramStats(programId),
    enabled: !!programId, // Ensure the query only runs if programId is provided
  });

  if (isLoading) {
    return (
      <Flex gap={4} mb={6} overflowX="auto" pb={2}>
        {Array.from({ length: 6 }).map((_, index) => (
          <StatCardSkeleton key={`stat-skeleton-${index}`} />
        ))}
      </Flex>
    );
  }

  if (isError || !data) {
    return (
      <Center py={6}>
        <Text color="red.500">{t('programs.programOverview.stats.errorLoading')}</Text>
      </Center>
    );
  }

  // Format percentage using Intl.NumberFormat to match other FormatNumber components
  const formatPercentage = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
    }).format(value / 100);
  };

  // Common props for all stat cards
  const cardProps = {
    minWidth: '180px',
    flex: 1,
    height: '140px',
    border: '1px solid',
    borderColor: 'gray.200',
  };

  return (
    <Flex gap={2} overflowX="auto" pb={2} width="100%" justifyContent="space-between">
      <StatCard
        title={t('programs.programOverview.stats.totalCommission')}
        description={t('programs.programOverview.stats.totalCommissionDesc')}
        value={data.commission}
        currency="EUR"
        {...cardProps}
      />

      <StatCard
        title={t('programs.programOverview.stats.totalOrders')}
        description={t('programs.programOverview.stats.totalOrdersDesc')}
        value={data.orders}
        {...cardProps}
      />

      <StatCard
        title={t('programs.programOverview.stats.totalVisits')}
        description={t('programs.programOverview.stats.totalVisitsDesc')}
        value={data.visits}
        {...cardProps}
      />

      <StatCard
        title={t('programs.programOverview.stats.totalSales')}
        description={t('programs.programOverview.stats.totalSalesDesc')}
        value={data.revenue}
        currency="EUR"
        {...cardProps}
      />

      <StatCard
        title={t('programs.programOverview.stats.conversionRate')}
        description={t('programs.programOverview.stats.conversionRateDesc')}
        value={formatPercentage(data.conversionRate)}
        {...cardProps}
      />
    </Flex>
  );
};

export default AffiliateProgramStats;
